import React from 'react';
import { Divider } from 'antd';
import type { FormInstance } from 'antd/es/form';

import TeamDropdownSelect, {
  type TeamDropdownSelectTeam,
} from '../../../components/TeamDropdownSelect';
import { CustomTitle } from '../../../components';
import type { ProductLookupItem } from '@/hooks/usePaginatedProductDropdown';
import { ProductMultiSelectField, ProductQuantityRows } from '../components';
import type { SelectedProduct } from '../components/ProductMultiSelectField';
import styles from '../BookShipmentDrawer.module.css';

export interface SpecialtyZoomlogiFieldsProps {
  token: string;
  form: FormInstance;
  teamLabel: string;
  showTeams: boolean;
  allTeams: TeamDropdownSelectTeam[];
  selectedTeams: TeamDropdownSelectTeam[];
  onTeamsChange: (teams: TeamDropdownSelectTeam[]) => void;
  teamsSearch: string;
  onTeamsSearch: (value: string) => void;
  selectedProducts: SelectedProduct[];
  onProductsChange: (products: SelectedProduct[]) => void;
  getProductName: (id: string) => string;
  mergeIntoLookup: (items: Array<{ id: string; name: string }>) => void;
  paginatedProducts: ProductLookupItem[];
  productCatalogTotalCount: number;
  productDropdownLoading: boolean;
  productSearchInputKey: number;
  productSearch: string;
  setProductSearch: (value: string) => void;
  handleProductPopupScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  updateProductQuantity: (id: string, delta: number) => void;
  removeProduct: (id: string | number) => void;
}

/**
 * Optional Zoomlogi-only Teams / Products for specialty book → CreateNewShipment.
 * Not sent to BioCair / Mercury / Life Couriers / MomentDash.
 */
const SpecialtyZoomlogiFields: React.FC<SpecialtyZoomlogiFieldsProps> = ({
  token,
  form,
  teamLabel,
  showTeams,
  allTeams,
  selectedTeams,
  onTeamsChange,
  teamsSearch,
  onTeamsSearch,
  selectedProducts,
  onProductsChange,
  getProductName,
  mergeIntoLookup,
  paginatedProducts,
  productCatalogTotalCount,
  productDropdownLoading,
  productSearchInputKey,
  productSearch,
  setProductSearch,
  handleProductPopupScroll,
  updateProductQuantity,
  removeProduct,
}) => {
  return (
    <>
      <Divider style={{ margin: '16px 0' }} />
      <CustomTitle
        text='Zoomlogi'
        fontSize='18px'
        fontWeight='700'
        lineHeight='24px'
        marginBottom={8}
      />
      <div className={styles.specialtyZoomlogiHint}>
        Optional. Assign teams and products on the Zoomlogi shipment after
        booking — not sent to the carrier.
      </div>
      {showTeams ? (
        <div className={styles.fieldWrap}>
          <label className={styles.label}>{`${teamLabel}s (optional)`}</label>
          <TeamDropdownSelect
            teamsOptions={allTeams}
            selectedTeams={selectedTeams}
            onChange={onTeamsChange}
            formState='add'
            onSearch={onTeamsSearch}
            teamsSearch={teamsSearch}
          />
        </div>
      ) : null}
      <div className={styles.fieldWrap}>
        <ProductMultiSelectField
          token={token}
          form={form}
          selectedProducts={selectedProducts}
          onProductsChange={onProductsChange}
          getProductName={getProductName}
          mergeIntoLookup={mergeIntoLookup}
          paginatedProducts={paginatedProducts}
          productCatalogTotalCount={productCatalogTotalCount}
          productDropdownLoading={productDropdownLoading}
          productSearchInputKey={productSearchInputKey}
          productSearch={productSearch}
          setProductSearch={setProductSearch}
          handlePopupScroll={handleProductPopupScroll}
          label='Products (optional)'
          ariaLabel='Products optional'
          fieldName={null}
        />
      </div>
      {selectedProducts.length > 0 ? (
        <div className={styles.fieldWrap}>
          <ProductQuantityRows
            selectedProducts={selectedProducts}
            setSelectedProducts={onProductsChange}
            getProductName={getProductName}
            updateQuantity={updateProductQuantity}
            removeProduct={removeProduct}
          />
        </div>
      ) : null}
    </>
  );
};

export default SpecialtyZoomlogiFields;
